import React from 'react'
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material'
import './GRIHeader.css'

/**
 * Reusable Report Table Component for GRI Reports
 * @param {Array} columns - Array of column objects with { field: string, label: string, width?: string }
 * @param {Array} rows - Array of row data objects
 * @param {string} title - Optional title above the table
 * @param {boolean} showSerialNumber - Whether to show S.No. column (default: true)
 * @param {boolean} showTotal - Whether to show a total row at the bottom (default: false)
 * @param {string|number} totalValue - The value to display in the total row
 */
const ReportTable = ({ 
  columns, 
  rows = [], 
  title = null,
  showSerialNumber = true,
  showTotal = false,
  totalValue = null
}) => {
  // Add S.No. column if showSerialNumber is true
  const tableColumns = showSerialNumber 
    ? [{ field: 'sno', label: 'S.No.', width: '60px' }, ...columns]
    : columns

  return (
    <Box sx={{ marginBottom: '32px' }}>
      {title && (
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#000000',
            marginBottom: '16px',
            fontFamily: 'Inter',
          }}
        >
          {title}
        </Typography>
      )}

      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 'none', 
          border: '1px solid #828282',
          borderRadius: '4px',
        }}
      >
        <Table sx={{ borderCollapse: 'collapse' }}>
          <TableHead sx={{ fontSize: '16px',textAlign: 'center' }}>
            <TableRow sx={{ backgroundColor: '#1C486B' }}>
              {tableColumns.map((column, index) => (
                <TableCell
                  key={index}
                  sx={{
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    padding: column.field === 'sno' ? '12px 8px' : '12px 16px',
                    width: column.width || 'auto',
                    minWidth: column.field === 'sno' ? '60px' : 'auto',
                    maxWidth: column.field === 'sno' ? '60px' : 'none',
                    textAlign: column.field === 'sno' ? 'center' : 'left',
                    whiteSpace: column.field === 'sno' ? 'nowrap' : 'normal',
                    overflow: column.field === 'sno' ? 'hidden' : 'visible',
                    border: '1px solid #828282',
                    borderBottom: '2px solid #828282',
                    
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows && rows.length > 0 ? (
              <>
                {rows.map((row, rowIndex) => (
                  <TableRow 
                    key={rowIndex}
                    sx={{
                      backgroundColor:  '#F2F6F8',
                    }}
                  >
                    {tableColumns.map((column, colIndex) => (
                      <TableCell 
                        key={colIndex}
                        sx={{ 
                          padding: column.field === 'sno' ? '12px 8px' : '12px 16px', 
                          fontSize: '16px',
                          color: '#000000',
                          border: '1px solid #828282',
                          backgroundColor: '#F2F6F8',
                          width: column.width || 'auto',
                          minWidth: column.field === 'sno' ? '60px' : 'auto',
                          maxWidth: column.field === 'sno' ? '60px' : 'none',
                          textAlign: column.field === 'sno' ? 'center' : 'left',
                          whiteSpace: column.field === 'sno' ? 'nowrap' : 'normal',
                          overflow: column.field === 'sno' ? 'hidden' : 'visible',
                        }}
                      >
                        {column.field === 'sno' 
                          ? `${rowIndex + 1}.`
                          : row[column.field] || '-'
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {showTotal && (
                  <TableRow 
                    sx={{
                      backgroundColor: '#F2F6F8',
                    }}
                  >
                    {/* Total row: span S.No. and Metrics columns, show totalValue in Value column */}
                    {showSerialNumber && (
                      <TableCell 
                        colSpan={2}
                        sx={{ 
                          padding: '12px 16px', 
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: '#000000',
                          border: '1px solid #828282',
                          backgroundColor: '#F2F6F8',
                        }}
                      >
                        Total
                      </TableCell>
                    )}
                    {!showSerialNumber && (
                      <TableCell 
                        colSpan={columns.length - 1}
                        sx={{ 
                          padding: '12px 16px', 
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: '#000000',
                          border: '1px solid #828282',
                          backgroundColor: '#F2F6F8',
                        }}
                      >
                        Total
                      </TableCell>
                    )}
                    {/* Value column */}
                    <TableCell 
                      sx={{ 
                        padding: '12px 16px', 
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#000000',
                        border: '1px solid #828282',
                        backgroundColor: '#F2F6F8',
                      }}
                    >
                      {totalValue !== null && totalValue !== undefined ? totalValue : '-'}
                    </TableCell>
                  </TableRow>
                )}
              </>
            ) : (
              <TableRow>
                <TableCell 
                  colSpan={tableColumns.length}
                  className="noDataCell"
                  sx={{
                    backgroundColor: '#F2F6F8',
                  }}
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default ReportTable
